import { RefreshCw } from "lucide-react";
import { Button } from "@ascleo/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@ascleo/ui/components/ui/dialog";

interface BrowserSetupScannerDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly scannerState: "idle" | "requesting" | "scanning" | "error" | "success";
  readonly scannerMessage: string;
  readonly onRetryScan: () => void;
  readonly onUseManualSetup: () => void;
  readonly setVideoElement: (element: HTMLVideoElement | null) => void;
}

/**
 * Phone-width single-column dialog for importing bridge values via QR scan.
 *
 * @since 0.0.0
 * @example
 * ```tsx
 * <BrowserSetupScannerDialog open={isScannerOpen} onOpenChange={setIsScannerOpen} ... />
 * ```
 */
export function BrowserSetupScannerDialog({
  open,
  onOpenChange,
  scannerState,
  scannerMessage,
  onRetryScan,
  onUseManualSetup,
  setVideoElement,
}: BrowserSetupScannerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogTitle>Bridge-QR-Code scannen</DialogTitle>
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-950">
            <video
              ref={setVideoElement}
              autoPlay
              muted
              playsInline
              className="aspect-[4/5] w-full object-cover"
              data-testid="frontdesk-web-scan-video"
            />
            {scannerState === "error" ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="absolute right-2 top-2"
                data-testid="frontdesk-web-retry-scan"
                onClick={onRetryScan}
                aria-label="Erneut versuchen"
              >
                <RefreshCw className="size-4" />
              </Button>
            ) : null}
          </div>
          <p
            className="text-sm text-slate-600"
            data-testid="frontdesk-web-scan-status"
            data-state={scannerState}
          >
            {scannerMessage}
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              data-testid="frontdesk-web-use-manual-setup"
              onClick={onUseManualSetup}
            >
              Stattdessen manuell eingeben
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
